/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useEffect } from "react"
import {
  BulkUploadTaskStatus,
  UploadType,
} from "types/delv-platform-api/bulkUploadTask"
import { useScribeBulkUploadNotifications } from "pages/menu/detail/bulkUpload/scribe/useScribeBulkUploadNotifications"
import { BULK_UPLOAD_TYPE } from "pages/menu/detail/bulkUpload/BulkUploadContext"
import { isUnsuccessfulTask } from "store/duck/bulkUpload/utils"
import _isEmpty from "lodash/isEmpty"
import FileValidationFailed from "pages/menu/detail/bulkUpload/bulkUploadNotifications/FileValidationFailed"
import UploadSuccessNoError from "./UploadSuccessNoError"
import UploadSuccessWithError from "./UploadSuccessWithError"
import UploadFailed from "./UploadFailed"
import ProcessingBanner from "./ProcessingBanner"
import { useCompletedBulkUploadTask } from "./hooks/useCompletedBulkUploadTask"
import { useFetchMenu } from "./hooks/useFetchMenu"

interface BulkUploadNotificationsProps {
  uploadType: UploadType
  bulkUploadType: BULK_UPLOAD_TYPE
}

const BulkUploadNotifications = ({
  uploadType,
  bulkUploadType,
}: BulkUploadNotificationsProps) => {
  const {
    completedBulkUploadTask,
    clearCompletedBulkUploadTask,
  } = useCompletedBulkUploadTask()
  const {
    trackSuccessfulShown,
    trackErrorFoundPageView,
    trackCancelClick,
  } = useScribeBulkUploadNotifications(bulkUploadType)
  const { status, statusCodes, errorReportURL } = completedBulkUploadTask || {}

  const isFullySucceeded = status === BulkUploadTaskStatus.TaskSucceeded
  const isUnsuccessful = isUnsuccessfulTask(completedBulkUploadTask)

  const isAllRowValidationFailed =
    isUnsuccessful && !_isEmpty(statusCodes?.rowValidations)
  const isFileValidationFailed =
    isUnsuccessful && !_isEmpty(statusCodes?.fileValidations)
  const isFailedTaskWithErrorReport =
    isUnsuccessful && !_isEmpty(errorReportURL)
  // File validation failures win over partial success so the two modals never stack.
  const isPartiallySucceeded =
    !isFileValidationFailed &&
    (isAllRowValidationFailed ||
      isFailedTaskWithErrorReport ||
      status === BulkUploadTaskStatus.TaskPartiallySucceeded)

  const isFailed =
    !isPartiallySucceeded && !isFileValidationFailed && isUnsuccessful

  const { fetchMenu } = useFetchMenu()

  useEffect(() => {
    if (!completedBulkUploadTask || !completedBulkUploadTask.taskID) return

    if (isFullySucceeded || isPartiallySucceeded) {
      fetchMenu()
      if (isFullySucceeded) {
        trackSuccessfulShown(completedBulkUploadTask.taskID)
      } else {
        trackErrorFoundPageView(completedBulkUploadTask.taskID)
      }
    }
  }, [
    isFullySucceeded,
    isPartiallySucceeded,
    completedBulkUploadTask,
    trackErrorFoundPageView,
    trackSuccessfulShown,
    fetchMenu,
  ])

  const uploadSuccessWithErrorOnCloseCallback = () => {
    clearCompletedBulkUploadTask()
    if (completedBulkUploadTask && completedBulkUploadTask.taskID)
      trackCancelClick(completedBulkUploadTask.taskID)
  }

  return (
    <div>
      <UploadSuccessNoError
        visible={isFullySucceeded}
        completedBulkUploadTask={completedBulkUploadTask}
        onCloseCallback={clearCompletedBulkUploadTask}
      />
      <UploadSuccessWithError
        visible={isPartiallySucceeded}
        completedBulkUploadTask={completedBulkUploadTask}
        onCloseCallback={uploadSuccessWithErrorOnCloseCallback}
        bulkUploadType={bulkUploadType}
      />
      <UploadFailed
        visible={isFailed}
        error={completedBulkUploadTask?.status}
        onCloseCallback={clearCompletedBulkUploadTask}
      />
      <FileValidationFailed
        visible={isFileValidationFailed}
        errors={completedBulkUploadTask?.statusCodes?.fileValidations}
        onCloseCallback={clearCompletedBulkUploadTask}
      />
      <ProcessingBanner uploadType={uploadType} />
    </div>
  )
}

export default BulkUploadNotifications
